import { useCallback, useEffect, useState } from "react";
import { getRecaptchaToken, loadRecaptcha } from "../api/recaptcha.js";
import { validatePassword } from "./usePasswordValidation.js";
import { ANALYTICS_EVENTS } from "../constants.js";
import { persistReturningUser } from "../utils/returningUser.js";
import { t } from "../utils/translate.js";
export function useSignUpStep2(api, config, email) {
    const [state, setState] = useState({
        email,
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        errors: {},
        isLoading: false,
        isCaptchaReady: !config.recaptchaSiteKey
    });
    useEffect(()=>{
        if (config.recaptchaSiteKey) {
            loadRecaptcha(config.recaptchaSiteKey).then(()=>setState((prev)=>({
                        ...prev,
                        isCaptchaReady: true
                    }))).catch(()=>setState((prev)=>({
                        ...prev,
                        isCaptchaReady: false
                    })));
        }
    }, [
        config.recaptchaSiteKey
    ]);
    useEffect(()=>{
        config.analyticsTrack?.(ANALYTICS_EVENTS.ABOUT_YOU_VIEW);
    }, []);
    const setField = useCallback((field, value)=>{
        setState((prev)=>({
                ...prev,
                [field]: value,
                errors: {
                    ...prev.errors,
                    [field]: undefined
                }
            }));
    }, []);
    const submit = useCallback(async ()=>{
        const errors = {};
        if (!state.firstName.trim()) {
            errors.firstName = t(config, 'auth.error.requiredField');
        }
        if (!state.lastName.trim()) {
            errors.lastName = t(config, 'auth.error.requiredField');
        }
        const passwordValidation = validatePassword(state.password);
        if (!passwordValidation.isValid) {
            errors.password = t(config, 'auth.signUp.passwordHint');
        }
        if (state.password !== state.confirmPassword) {
            errors.confirmPassword = t(config, 'auth.error.passwordMismatch');
        }
        if (Object.keys(errors).length > 0) {
            setState((prev)=>({
                    ...prev,
                    errors
                }));
            return;
        }
        setState((prev)=>({
                ...prev,
                isLoading: true,
                errors: {}
            }));
        try {
            let recaptchaToken;
            if (config.recaptchaSiteKey) {
                recaptchaToken = await getRecaptchaToken(config.recaptchaSiteKey);
            }
            const { result, error } = await api.register(state.email, state.firstName.trim(), state.lastName.trim(), state.password, recaptchaToken);
            if (error) {
                if (error.code === 'DISPOSABLE_EMAIL') {
                    config.analyticsTrack?.(ANALYTICS_EVENTS.SIGNUP_DISPOSABLE_EMAIL_BLOCKED, {
                        emailDomain: state.email.split('@')[1]
                    });
                }
                const errorMessage = error.code === 'REGISTRATION_FAILED' ? t(config, 'auth.error.registrationFailed') : error.code === 'RECAPTCHA_FAILED' ? t(config, 'auth.error.recaptchaFailed') : error.code === 'RATE_LIMITED' ? t(config, 'auth.error.rateLimited') : error.code === 'DISPOSABLE_EMAIL' ? t(config, 'auth.error.disposableEmail') : error.message;
                setState((prev)=>({
                        ...prev,
                        isLoading: false,
                        errors: {
                            form: errorMessage
                        }
                    }));
                return;
            }
            if (result?.requiresAction) {
                setState((prev)=>({
                        ...prev,
                        isLoading: false
                    }));
                config.onRequiresAction?.();
                return;
            }
            if (result?.tokens) {
                persistReturningUser(result.tokens, state.firstName, state.lastName);
                setState((prev)=>({
                        ...prev,
                        isLoading: false
                    }));
                config.onRegisterSuccess(result.tokens);
            }
        } catch  {
            setState((prev)=>({
                    ...prev,
                    isLoading: false,
                    errors: {
                        form: t(config, 'auth.error.networkError')
                    }
                }));
        }
    }, [
        state,
        api,
        config
    ]);
    const isFormValid = state.firstName.trim() !== '' && state.lastName.trim() !== '' && validatePassword(state.password).isValid && state.password === state.confirmPassword && state.isCaptchaReady;
    return {
        email: state.email,
        firstName: state.firstName,
        lastName: state.lastName,
        password: state.password,
        confirmPassword: state.confirmPassword,
        errors: state.errors,
        isLoading: state.isLoading,
        isCaptchaReady: state.isCaptchaReady,
        isFormValid,
        setField,
        submit
    };
}

//# sourceMappingURL=useSignUpStep2.js.map